import { useEffect, useState } from 'react';
import Styles from '../assets/css/productEdit.module.scss';
import { data, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { popUpBoxTrigger } from "../features/message/popUpSlice";
import PopUpMessage from "../utils/errors/PopUpMessage";
import { getProductDetailsById, updateProduct, warehouseList, allCompanyList, getCategories, uploadImages } from '../services/Api';

// Custom Hooks
import { useProductForm } from '../hooks/useProductForm';
import { useAttributes } from '../hooks/useAttributes';
import { useBrands } from '../hooks/useBrands';

// Components
import ProductBasicInfo from '../components/productAdd/components/ProductBasicInfo/ProductBasicInfo';
import CategorySelector from '../components/productAdd/components/CategorySelector/CategorySelector';
import BrandSelector from '../components/productAdd/components/BrandSelector/BrandSelector';
import AttributeFields from '../components/productAdd/components/AttributeFields/AttributeFields';
import MultipleSKU from '../components/productAdd/components/MultipleSKU/MultipleSKU';
import ImageUploader from '../components/productAdd/components/ImageUploader/ImageUploader';
import PerishableFields from '../components/productAdd/components/PerishableFields/PerishableFields';
import BarcodeField from '../components/productAdd/components/BarcodeField/BarcodeField';
import CustomCKEditor from '../components/common/CKEditor/CKEditor';
import FormButtons from '../components/productAdd/components/FormButtons/FormButtons';
import Loader from '../utils/Loader';

const ProductEdit = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  // Form state management
  const {
    formData,
    setFormData,
    updateFormData,
    errors,
    setErrors,
    clearError,
    isBarcodeFromScan,
    setIsBarcodeFromScan,
    enableManualBarcodeEdit,
    transactionId
  } = useProductForm();

  // Local state
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [warehouses, setWarehouses] = useState([]);
  const [companies, setCompanies] = useState([]);

  // Category state
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [selectedCategoryName, setSelectedCategoryName] = useState('');

  // Brand state
  const [brandSearchTerm, setBrandSearchTerm] = useState('');
  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [existingProductImageId, setExistingProductImageId] = useState(null);

  // SKU state
  const [skus, setSkus] = useState([]);

  // Attributes management
  const {
    attributes,
    attributeValues,
    mandatoryAttributes,
    skuAttributes,
    setAttributeValues,
    loadingAttributes,
    fieldsToHide,
    shouldHideField,
    fetchAttributes
  } = useAttributes();

  // Filter out propCascade and only show mandatory attributes
  const filteredMandatoryAttributes = mandatoryAttributes.filter(
    attr => attr.name !== "propCascade" && true
  );

  // Brands hook
  const {
    brandSuggestions,
    loading: brandsLoading,
    searchBrands
  } = useBrands();

  // Initialize data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [warehousesResponse, companiesResponse] = await Promise.all([
          warehouseList(),
          allCompanyList({})
        ]);
        setWarehouses(warehousesResponse.data);
        setCompanies(companiesResponse.data);
      } catch (error) {
        showPopup('error', 'Error', 'Failed to load initial data');
      }
    };

    fetchInitialData();
  }, []);

  // Extract all unique images from SKUs
  const getAllSkuImages = () => {
    const allImages = new Set();
    skus.forEach(sku => {
      if (sku.Images && Array.isArray(sku.Images.Image)) {
        sku.Images.Image.forEach(img => {
          if (img && img.startsWith('http')) {
            allImages.add(img);
          }
        });
      }
    });
    return Array.from(allImages).slice(0, 5); // Max 5 images
  };

  // Fetch product details
  useEffect(() => {
    const fetchProductDetails = async () => {
      if (!id) {
        showPopup('error', 'Error', 'Invalid Product ID');
        navigate('/products');
        return;
      }

      setIsLoading(true);
      try {
        const response = await getProductDetailsById(id);
        if (response.status === 200) {
          const product = response.data;

          // Set basic form data
          setFormData({
            company_id: product.company_id?.toString() || '',
            warehouse_id: product.warehouse_id?.toString() || '',
            product_name: product.product_name || '',
            description: product.lazada_product?.data?.attributes?.description || '',
            storage_type: product.storage_type || 'dry',
            expire_date: product.expire_date || '',
            barcode_number: product.barcode_number || '',
            category_id: product.lazada_product?.data?.primary_category?.toString() || '',
            productType: product.storage_type === 'frozen' ? 'perishable' : 'non-perishable'
          });

          // Set category
          if (product.lazada_product?.data?.primary_category) {
            const categoryId = product.lazada_product.data.primary_category;
            setCategorySearchTerm(categoryId.toString());

            // Fetch category name using product name
            const productName = product.lazada_product?.data?.attributes?.name || product.product_name;
            if (productName) {
              try {
                const categoryResponse = await getCategories(productName);
                if (categoryResponse && categoryResponse.status === 200) {
                  const categories = categoryResponse.data?.data?.categorySuggestions || [];
                  const matchedCategory = categories.find(cat => cat.categoryId === categoryId);
                  if (matchedCategory) {
                    setSelectedCategoryName(matchedCategory.categoryName);
                    setCategorySearchTerm(matchedCategory.categoryName); // Set the name as search term
                  } else {
                    setSelectedCategoryName(categoryId.toString()); // Fallback to ID if name not found
                  }
                }
              } catch (error) {
                console.error('Error fetching category name:', error);
                setSelectedCategoryName(categoryId.toString()); // Fallback to ID on error
              }
            }

            // Fetch attributes for the category
            await fetchAttributes(product.lazada_product.data.primary_category);
          }

          const productImages = response.data.product_image;

          // Get only the IDs
          const existingProductIds = productImages.map(item => item.id);
          setExistingProductImageId(existingProductIds)

          // Set brand
          const brandName = extractBrandName(product);
          setBrandSearchTerm(brandName);
          updateFormData('brand_name', brandName);

          // Set SKUs with proper image handling
          if (product.lazada_product?.data?.skus) {
            const productSkus = product.lazada_product.data.skus.map(sku => ({
              SellerSku: sku.SellerSku,
              quantity: sku.quantity?.toString() || "1",
              price: sku.price?.toString() || "",
              package_height: sku.package_height?.toString() || "",
              package_length: sku.package_length?.toString() || "",
              package_width: sku.package_width?.toString() || "",
              package_weight: sku.package_weight?.toString() || "",
              Images: {
                Image: product.lazada_product.data.skus.length > 1
                  ? (Array.isArray(sku.Images) ? sku.Images : [])
                  : (product.lazada_product?.data?.images || [])
              },
              saleProp: sku.saleProp || {},
              package_content: sku.package_content || "Single Item",
              tax_class: "default",
              Status: sku.Status || "active",
              SkuId: sku.SkuId?.toString() || ""
            }));
            setSkus(productSkus);
          }

          // Set attribute values from product data - ONLY MANDATORY FIELDS
          if (product.lazada_product?.data?.attributes) {
            const lazadaAttributes = product.lazada_product.data.attributes;
            const initialAttributeValues = {};

            // Loop through all attributes and set their values
            Object.keys(lazadaAttributes).forEach(attrName => {
              if (lazadaAttributes[attrName] !== undefined && lazadaAttributes[attrName] !== null) {
                initialAttributeValues[attrName] = lazadaAttributes[attrName];
              }
            });

            // Optionally, override product name and brand if available
            if (lazadaAttributes.name && !product.product_name) {
              updateFormData('product_name', lazadaAttributes.name);
            }

            if (lazadaAttributes.brand && !brandName) {
              setBrandSearchTerm(lazadaAttributes.brand);
              updateFormData('brand_name', lazadaAttributes.brand);
            }

            setAttributeValues(prev => ({
              ...prev,
              ...initialAttributeValues
            }));
          }

        } else {
          showPopup('error', 'Error', 'Product not found');
          navigate('/products');
        }
      } catch (error) {
        console.error('Error fetching product details:', error);
        showPopup('error', 'Error', 'Failed to fetch product details');
        navigate('/products');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProductDetails();
  }, [id, navigate]);

  // Extract brand name from product data
  const extractBrandName = (product) => {
    if (product.brand_name && product.brand_name.trim() !== '') {
      return product.brand_name;
    }
    if (product.lazada_product?.data?.attributes?.brand) {
      return product.lazada_product.data.attributes.brand;
    }
    if (product.description) {
      const brandMatch = product.description.match(/<td>Brand<\/td><td>([^<]+)<\/td>/);
      if (brandMatch && brandMatch[1]) {
        return brandMatch[1];
      }
    }
    return '';
  };

  // Brand search handler
  const handleBrandSearch = (searchTerm) => {
    searchBrands(searchTerm);
  };

  // Brand selection handler
  const handleBrandSelect = (brand) => {
    setSelectedBrand(brand);
    updateFormData('brand_id', brand.brand_id ? brand.brand_id.toString() : '');
    setBrandSearchTerm(brand.name);
    setShowBrandDropdown(false);
    updateFormData('brand_name', brand.name);

    if (errors.brand_name) {
      setErrors(prev => ({ ...prev, brand_name: '' }));
    }

    setAttributeValues(prev => ({
      ...prev,
      brand: brand.name
    }));
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Company
    if (!formData.company_id) {
      newErrors.company_id = 'Company is required';
    }

    // Category
    if (!formData.category_id) {
      newErrors.category_id = 'Category is required';
    }

    // Brand Name - UPDATED: Check if brand is required based on attributes
    const brandAttribute = attributes.find(attr => attr.name === 'brand');
    const isBrandMandatory = brandAttribute && brandAttribute.is_mandatory === 1;

    let brandName = formData.brand_name;
    let brandFound = false;

    // Check if brand_name exists in formData
    if (brandName && typeof brandName === 'object' && brandName.name) {
      brandName = brandName.name;
      brandFound = true;
    } else if (brandName && typeof brandName === 'string' && brandName.trim() !== '') {
      brandFound = true;
    }

    // If not found in formData, check attributeValues for brand
    if (!brandFound && attributeValues.brand) {
      const attributeBrand = attributeValues.brand;
      if (attributeBrand && typeof attributeBrand === 'object' && attributeBrand.name) {
        brandName = attributeBrand.name;
        brandFound = true;
      } else if (attributeBrand && typeof attributeBrand === 'string' && attributeBrand.trim() !== '') {
        brandName = attributeBrand;
        brandFound = true;
      }
    }

    // Also check if we have a selected brand
    if (!brandFound && selectedBrand && selectedBrand.name && selectedBrand.name.trim() !== '') {
      brandName = selectedBrand.name;
      brandFound = true;
    }

    // Final brand validation - ONLY if brand is mandatory OR we have brand attribute
    if ((isBrandMandatory || brandAttribute) && (!brandFound || !brandName || brandName.trim() === '')) {
      // Set error only on the main brand field, not on attribute_brand
      newErrors.brand_name = 'Brand name is required';
    }

    // Product Name
    const productNameFromForm = formData.product_name;
    const productNameFromAttributes = attributeValues.name;
    const productName = productNameFromForm || productNameFromAttributes;

    // Check if product name is mandatory
    const nameAttribute = filteredMandatoryAttributes.find(attr => attr.name === 'name');
    const isNameMandatory = nameAttribute && nameAttribute.is_mandatory === 1;

    if (isNameMandatory && (!productName || productName.trim() === '')) {
      newErrors.product_name = 'Product name is required';
    }

    // Description
    if (!formData.description || formData.description.trim() === '') {
      newErrors.description = 'Description is required';
    }

    // Warehouse
    if (!formData.warehouse_id) {
      newErrors.warehouse_id = 'Warehouse is required';
    }

    // Barcode
    if (!formData.barcode_number || formData.barcode_number.trim() === '') {
      newErrors.barcode_number = 'Barcode is required';
    }

    // Mandatory Attribute validation - UPDATED: Skip brand attribute as we already handled it
    const mandatoryAttributes = filteredMandatoryAttributes.filter(attr => attr.is_mandatory === 1);

    mandatoryAttributes.forEach(attr => {
      // Skip brand attribute validation as we already handled it above
      if (attr.name === 'brand') {
        return;
      }

      const value = attributeValues[attr.name];
      const isEmpty = value === null || value === undefined || value === '' || value === '0' || value === 'undefined';

      if (isEmpty) {
        newErrors[`attribute_${attr.name}`] = `${attr.label} is required`;
      }
    });

    // Validate SKUs
    skus.forEach((sku, index) => {
      if (!sku.SellerSku || sku.SellerSku.trim() === '') {
        newErrors[`skus.${index}.SellerSku`] = 'Seller SKU is required';
      }

      if (!sku.quantity || sku.quantity.trim() === '' || parseInt(sku.quantity) <= 0) {
        newErrors[`skus.${index}.quantity`] = 'Valid quantity is required';
      }

      if (!sku.price || sku.price.trim() === '' || parseFloat(sku.price) <= 0) {
        newErrors[`skus.${index}.price`] = 'Valid price is required';
      }

      if (!sku.package_weight || sku.package_weight.trim() === '') {
        newErrors[`skus.${index}.package_weight`] = 'Package weight is required';
      }

      if (!sku.package_length || sku.package_length.trim() === '') {
        newErrors[`skus.${index}.package_length`] = 'Package length is required';
      }

      if (!sku.package_width || sku.package_width.trim() === '') {
        newErrors[`skus.${index}.package_width`] = 'Package width is required';
      }

      if (!sku.package_height || sku.package_height.trim() === '') {
        newErrors[`skus.${index}.package_height`] = 'Package height is required';
      }

      // Validate SKU images (max 5 per SKU)
      if (sku.Images && Array.isArray(sku.Images.Image) && sku.Images.Image.length > 5) {
        newErrors[`skus.${index}.images`] = 'Maximum 5 images allowed per SKU';
      }
    });

    // Perishable product validation
    if (formData.productType === 'perishable') {
      if (!formData.storage_type) {
        newErrors.storage_type = 'Storage type is required';
      }
      if (!formData.expire_date) {
        newErrors.expire_date = 'Expiry date is required';
      } else {
        const expireDate = new Date(formData.expire_date);
        if (expireDate <= today) {
          newErrors.expire_date = 'Expiry date must be after today';
        }
      }
    }

    return newErrors;
  };

  const showPopup = (type, heading, message) => {
    dispatch(popUpBoxTrigger({ type, heading, message, show: true }));
  };

  const handleCategorySelect = async (category) => {
    setSelectedCategoryName(category.categoryName);
    updateFormData('category_id', category.categoryId);
    setCategorySearchTerm(category.categoryName);

    if (errors.category_id) {
      setErrors(prev => ({ ...prev, category_id: '' }));
    }

    await fetchAttributes(category.categoryId);
  };

  const handleAttributeChange = (attributeName, value) => {
    const attribute = attributes.find(attr => attr.name === attributeName);

    if (attribute && (attribute.input_type === 'multiSelect' || attribute.input_type === 'multiEnumInput')) {
      setAttributeValues(prev => ({
        ...prev,
        [attributeName]: Array.isArray(value) ? value : (value ? [value] : [])
      }));
    } else {
      setAttributeValues(prev => ({
        ...prev,
        [attributeName]: value
      }));
    }

    if (attributeName === 'name') {
      updateFormData('product_name', value);
    }

    if (attributeName === 'long_description' || attributeName === 'Long Description') {
      updateFormData('long_description', value);
    }

    if (attributeName === 'brand') {
      if (value && typeof value === 'object' && value.name) {
        updateFormData('brand_name', value.name);
        if (value.brand_id) {
          updateFormData('brand_id', value.brand_id.toString());
        }
      } else {
        updateFormData('brand_name', value);
      }

      if (selectedBrand && selectedBrand.brand_id) {
        updateFormData('brand_id', selectedBrand.brand_id.toString());
      }
    }

    const errorKey = `attribute_${attributeName}`;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: '' }));
    }
  };

  const clearAttributeError = (attributeName) => {
    const errorKey = `attribute_${attributeName}`;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    console.log(existingProductImageId, 'existingProductIds');

    e.preventDefault();
    e.stopPropagation();

    // Sync data from attributes to form state
    if (!formData.brand_name && attributeValues.brand) {
      const brandValue = attributeValues.brand;
      if (typeof brandValue === 'object' && brandValue.name) {
        updateFormData('brand_name', brandValue.name);
        if (brandValue.brand_id) {
          updateFormData('brand_id', brandValue.brand_id.toString());
        }
      } else if (typeof brandValue === 'string') {
        updateFormData('brand_name', brandValue);
      }
    }

    if (formData.brand_name && typeof formData.brand_name === 'object') {
      const brandObj = formData.brand_name;
      updateFormData('brand_name', brandObj.name || '');
      if (brandObj.brand_id) {
        updateFormData('brand_id', brandObj.brand_id.toString());
      }
    }

    if (!formData.brand_id && selectedBrand && selectedBrand.brand_id) {
      updateFormData('brand_id', selectedBrand.brand_id.toString());
    }

    // Wait for state updates
    await new Promise(resolve => setTimeout(resolve, 100));

    const formErrors = validateForm();

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);

      const errorFields = Object.keys(formErrors);
      let errorMessage = 'Please fill all required fields correctly';

      if (errorFields.length > 0 && errorFields.length <= 3) {
        // Field name mapping for user-friendly display and duplicate prevention
        const fieldNameMap = {
          'company_id': 'Company',
          'warehouse_id': 'Warehouse',
          'category_id': 'Category',
          'brand_name': 'Brand name',
          'attribute_brand': 'Brand name', // Map attribute_brand to same display name
          'product_name': 'Product name',
          'description': 'Description',
          'barcode_number': 'Barcode',
          'storage_type': 'Storage type',
          'expire_date': 'Expiry date',
          'SellerSku': 'Seller SKU',
          'quantity': 'Quantity',
          'price': 'Price',
          'package_weight': 'Package weight',
          'package_length': 'Package length',
          'package_width': 'Package width',
          'package_height': 'Package height'
        };

        // Use Set to remove duplicates based on mapped field names
        const uniqueErrors = new Set();
        const firstErrors = errorFields.slice(0, 3).map(field => {
          // Extract clean field name
          let cleanFieldName = field
            .replace('attribute_', '')
            .replace('skus.0.', '')
            .replace(/\..*$/, ''); // Remove everything after dot

          // Use mapped name or generate from the field
          cleanFieldName = fieldNameMap[cleanFieldName] ||
            cleanFieldName.replace(/_/g, ' ')
              .replace(/\b\w/g, l => l.toUpperCase());

          const errorMessage = `${cleanFieldName} is required`;

          // Add to set to ensure uniqueness
          if (!uniqueErrors.has(errorMessage)) {
            uniqueErrors.add(errorMessage);
            return errorMessage;
          }
          return null;
        }).filter(Boolean); // Remove null values

        if (firstErrors.length > 0) {
          errorMessage = `Please fix the following: ${firstErrors.join('. ')}.`;
        }
      }

      showPopup('error', 'Validation Error', errorMessage);
      return;
    }

    setLoading(true);
    try {
      // Build saleProp for SKU
      const buildSaleProp = (sku) => {
        const saleProp = {};
        if (sku.saleProp && Object.keys(sku.saleProp).length > 0) {
          Object.keys(sku.saleProp).forEach(attrName => {
            const value = sku.saleProp[attrName];
            if (value && value.trim() !== '') {
              saleProp[attrName] = value;
            }
          });
        }
        return Object.keys(saleProp).length > 0 ? saleProp : undefined;
      };

      // Prepare SKUs data
      const finalSkus = skus.map(sku => {
        const skuImages = (sku.Images?.Image || []).filter(url =>
          url && url.startsWith('http') && !url.startsWith('blob:')
        ).slice(0, 5); // Ensure max 5 images per SKU

        return {
          SellerSku: sku.SellerSku,
          quantity: sku.quantity.toString(),
          price: sku.price.toString(),
          package_height: sku.package_height.toString(),
          package_length: sku.package_length.toString(),
          package_width: sku.package_width.toString(),
          package_weight: sku.package_weight.toString(),
          Images: {
            Image: skuImages
          },
          ...(buildSaleProp(sku) && {
            saleProp: buildSaleProp(sku)
          }),
          package_content: sku.package_content,
          tax_class: sku.tax_class,
          Status: sku.Status,
          ...(sku.SkuId && { SkuId: sku.SkuId })
        };
      });

      // Extract brand information
      let brandName = formData.brand_name || attributeValues.brand || 'No Brand';
      let brandId = formData.brand_id || '';

      if (brandName && typeof brandName === 'object' && brandName.name) {
        brandName = brandName.name;
        if (formData.brand_name && typeof formData.brand_name === 'object' && formData.brand_name.brand_id && !brandId) {
          brandId = formData.brand_name.brand_id.toString();
        }
      }

      if (attributeValues.brand && typeof attributeValues.brand === 'object' && attributeValues.brand.name) {
        brandName = attributeValues.brand.name;
        if (attributeValues.brand.brand_id && !brandId) {
          brandId = attributeValues.brand.brand_id.toString();
        }
      }

      if (!brandId && selectedBrand && selectedBrand.brand_id) {
        brandId = selectedBrand.brand_id.toString();
      }

      if (!brandId && formData.brand_id) {
        brandId = formData.brand_id.toString();
      }

      // const productName = formData.product_name || attributeValues.name || '';

      // Get ALL images from ALL SKUs for product_images
      const allProductImages = getAllSkuImages();

      if (allProductImages.length === 0) {
        showPopup('error', 'Error', 'At least one product image is required from SKU variations');
        setLoading(false);
        return;
      }

      const firstSkuImage = allProductImages[0];
      const barcodeImageUrl = firstSkuImage ||
        `https://via.placeholder.com/400x400/ffffff/000000.png?text=BARCODE:${encodeURIComponent(formData.barcode_number)}`;

      // Build attributes object - ONLY MANDATORY ATTRIBUTES
      const attributesObj = {
        name: formData.product_name,
        description: formData.description,
        ...Object.keys(attributeValues).reduce((acc, key) => {
          const value = attributeValues[key];

          // Only include mandatory attributes
          const isMandatory = filteredMandatoryAttributes.some(attr => attr.name === key);
          if (!isMandatory) return acc;

          const skuFields = ['SellerSku', 'sku', 'price', 'package_weight', 'package_length', 'package_width', 'package_height', 'quantity'];
          if (skuFields.includes(key)) return acc;

          const saleProperties = ['color_family'];
          if (saleProperties.includes(key)) return acc;

          if (key === 'brand') return acc;

          if (value !== null && value !== undefined && value !== '' && value !== '0' && value !== 'undefined') {
            const attribute = attributes.find(attr => attr.name === key);
            if (attribute) {
              if (key.toLowerCase() === 'hazmat') {
                if (Array.isArray(value)) {
                  if (value.length === 0) {
                    acc[key] = "None";
                  } else {
                    const hazmatNames = value.map(val => {
                      if (attribute.options) {
                        const selectedOption = attribute.options.find(opt =>
                          opt.id.toString() === val.toString() || opt.name === val
                        );
                        return selectedOption ? selectedOption.name : val;
                      }
                      return val;
                    });
                    acc[key] = hazmatNames.length === 1 ? hazmatNames[0] : hazmatNames.join(', ');
                  }
                } else {
                  acc[key] = value.toString();
                }
              }
              else if ((attribute.input_type === 'multiSelect' || attribute.input_type === 'multiEnumInput') && Array.isArray(value)) {

                if (value.length > 0) {
                  const multiSelectNames = value.map(val => {
                    if (attribute.options) {
                      const selectedOption = attribute.options.find(opt =>
                        opt.id.toString() === val.toString() || opt.name === val
                      );
                      return selectedOption ? selectedOption.name : val;
                    }
                    return val;
                  });
                  acc[key] = multiSelectNames.join(',');
                } else {
                  acc[key] = '';
                }
              }
              else if ((attribute.input_type === 'singleSelect' || attribute.input_type === 'enumInput') && attribute.options) {
                const selectedOption = attribute.options.find(opt =>
                  opt.id.toString() === value.toString() || opt.name === value
                );
                if (selectedOption) {
                  acc[key] = selectedOption.name;
                } else {
                  acc[key] = value;
                }
              }
              else if (attribute.input_type === 'textInput') {
                acc[key] = value.toString();
              } else if (attribute.input_type === 'numericInput') {
                acc[key] = parseFloat(value) || 0;
              } else {
                acc[key] = value;
              }
            } else {
              acc[key] = value;
            }
          }
          return acc;
        }, {})
      };

      if (brandId && brandId !== '') {
        attributesObj.brand_id = brandId;
      }

      const storageTypeValue = formData.productType === 'perishable'
        ? (formData.storage_type || 'dry')
        : 'dry';

      const expireDateValue = formData.productType === 'perishable'
        ? (formData.expire_date || '2099-12-31')
        : '2099-12-31';

      // Get existing product IDs from the original product data
      // const ids = productImages.map(item => item.id);
      // const existingProductIds = [ids];

      // Build the final payload for update - FIXED: Proper array format for existing_product_ids
      const payload = {
        id: id.toString(),
        company_id: parseInt(formData.company_id),
        product_name: formData.product_name,
        storage_type: storageTypeValue,
        warehouse_id: parseInt(formData.warehouse_id),
        expire_date: expireDateValue,
        status: 1,
        barcode_number: formData.barcode_number.toString(),
        barcode_image: barcodeImageUrl,
        quantity: finalSkus.reduce((total, sku) => total + parseInt(sku.quantity), 0),
        e_commerce_platform: 'lazada',
        is_new_transection: 'false',

        // FIX: Send product_images as array of image URLs from SKUs
        product_images: allProductImages,

        // FIX: Send existing_product_ids as proper array
        existing_product_ids: existingProductImageId, // This should be an array of IDs

        Request: {
          Product: {
            PrimaryCategory: formData.category_id.toString(),
            AssociatedSku: finalSkus[0]?.SkuId || finalSkus[0]?.SellerSku || '',
            item_id: parseInt(id),
            Images: {
              Image: allProductImages
            },
            Attributes: attributesObj,
            Skus: {
              Sku: finalSkus
            }
          }
        }
      };

      console.log('Update Payload:', JSON.stringify(payload, null, 2));
      // return payload;

      const response = await updateProduct(payload);
      if (response.status === 200) {
        showPopup('success', 'Success', 'Product updated successfully');
        setTimeout(() => {
          navigate('/products');
        }, 1000);
      } else {
        showPopup('error', 'Error', response.message || 'Failed to update product');
      }
    } catch (error) {
      console.error('API Error:', error);
      if (error.response?.data) {
        const errorData = error.response.data;
        let errorMessage = errorData.message || 'Failed to update product';
        if (errorData.errors) {
          Object.values(errorData.errors).forEach(errArray => {
            if (Array.isArray(errArray)) {
              errArray.forEach(err => {
                errorMessage += `\n${err}`;
              });
            }
          });
        }
        showPopup('error', 'Update Product Failed', errorMessage);
      } else {
        showPopup('error', 'Error', error.message || 'Failed to update product');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'barcode_number' && isBarcodeFromScan) {
      return;
    }

    updateFormData(name, value);

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    if (name === 'productType' && value === 'non-perishable') {
      updateFormData('storage_type', '');
      updateFormData('expire_date', '');
      if (errors.storage_type || errors.expire_date) {
        setErrors(prev => ({ ...prev, storage_type: '', expire_date: '' }));
      }
    }
  };

  return (
    <div className={`${Styles.product_edit}`}>
      <PopUpMessage />
      <div className={`${Styles.product_heading}`}>
        <h2>Edit Product</h2>
      </div>

      {isLoading ? (
        <div className={`${Styles.product_form} ${Styles.loading_form}`}>
          <div className={`${Styles.form_loader_container}`}>
            <Loader size="large" color="primary" />
          </div>
        </div>
      ) : (
        <div className={`${Styles.product_form}`}>
          <div className={`${Styles.product_details} ${loading ? Styles.loading : ''}`}>
            <div className={`${Styles.title}`}>
              <h4>Product details</h4>
              <hr />
            </div>
            <div className={`${Styles.form}`}>
              <ProductBasicInfo
                formData={formData}
                errors={errors}
                companies={companies}
                warehouses={warehouses}
                updateFormData={updateFormData}
                clearError={clearError}
              />

              <CategorySelector
                formData={formData}
                errors={errors}
                onCategorySelect={handleCategorySelect}
                categorySearchTerm={categorySearchTerm}
                setCategorySearchTerm={setCategorySearchTerm}
                selectedCategoryName={selectedCategoryName}
                setSelectedCategoryName={setSelectedCategoryName}
                clearError={clearError}
                showPopup={showPopup}
              />

              {loadingAttributes && (
                <div className={`${Styles.loading_container}`}>
                  <p>Loading attributes...</p>
                </div>
              )}

              {/* Mandatory Attributes Only */}
              <AttributeFields
                attributes={filteredMandatoryAttributes}
                attributeValues={attributeValues}
                errors={errors}
                onAttributeChange={handleAttributeChange}
                clearAttributeError={clearAttributeError}
                loadingAttributes={loadingAttributes}
              />

              {/* Brand Selector */}
              {/* <BrandSelector
              formData={formData}
              errors={errors}
              brandSuggestions={brandSuggestions}
              brandsLoading={brandsLoading}
              onBrandSearch={handleBrandSearch}
              onBrandSelect={handleBrandSelect}
              brandSearchTerm={brandSearchTerm}
              setBrandSearchTerm={setBrandSearchTerm}
              showBrandDropdown={showBrandDropdown}
              setShowBrandDropdown={setShowBrandDropdown}
              selectedBrand={selectedBrand}
              setSelectedBrand={setSelectedBrand}
              updateFormData={updateFormData}
              clearError={clearError}
              fieldsToHide={fieldsToHide}
            /> */}

              {/* Multiple SKU Component */}
              <div className={`${Styles.sku_section}`}>
                <MultipleSKU
                  skus={skus}
                  setSkus={setSkus}
                  skuAttributes={skuAttributes}
                  errors={errors}
                  previewImages={[]}
                  showPopup={showPopup}
                  isEdit={true}
                />
              </div>

              {/* Description */}
              <div className={`${Styles.label_input} ${errors.description ? Styles.error : ''}`}>
                <label>Description<span>*</span></label>
                <div className={`${Styles.input_container}`}>
                  <CustomCKEditor
                    value={formData.description}
                    onChange={(value) => {
                      updateFormData('description', value);
                      clearError('description');
                    }}
                    onFocus={() => clearError('description')}
                    error={errors.description}
                    placeholder="Enter Product Description"
                    height="200px"
                  />
                  {errors.description && <span className={`${Styles.errorMessage}`}>{errors.description}</span>}
                </div>
              </div>

              {/* Long Description from Attributes */}
              {(attributeValues['long_description'] || attributeValues['Long Description']) && (
                <div className={`${Styles.label_input} ${errors.long_description ? Styles.error : ''}`}>
                  <label>Long Description</label>
                  <div className={`${Styles.input_container}`}>
                    <CustomCKEditor
                      value={attributeValues['long_description'] || attributeValues['Long Description'] || ''}
                      onChange={(value) => {
                        handleAttributeChange('long_description', value);
                        // Also update the other possible key name
                        handleAttributeChange('Long Description', value);
                      }}
                      placeholder="Enter Long Description"
                      height="200px"
                    />
                  </div>
                </div>
              )}

              <PerishableFields
                formData={formData}
                errors={errors}
                updateFormData={updateFormData}
                clearError={clearError}
              />

              <BarcodeField
                formData={formData}
                errors={errors}
                isBarcodeFromScan={isBarcodeFromScan}
                updateFormData={updateFormData}
                onEnableEdit={enableManualBarcodeEdit}
                clearError={clearError}
                disabled={true}
              />
            </div>

            <FormButtons
              loading={loading}
              onSubmit={handleSubmit}
              transactionId={transactionId}
              isEdit={true}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductEdit;